import { useEffect, useRef, useCallback } from 'react';


export interface UseSelectorPaginationOptions {
  /** Reference to the scrollable list element */
  listRef: React.RefObject<HTMLUListElement | null>;
  /** Whether there are more pages to load */
  hasNextPage: boolean;
  /** Whether a load operation is in progress */
  isLoadingMore: boolean;
  /** Callback to load the next page */
  onLoadMore?: () => Promise<void>;
  /** Whether the dropdown is open */
  open: boolean;
  /** Threshold in pixels before the end to trigger loading (default: 50) */
  threshold?: number;
}

export interface UseSelectorPaginationReturn {
  /** Reference to attach to the sentinel element at the bottom of the list */
  sentinelRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Determines if loading should be triggered based on intersection entry and current state.
 * Pure function extracted for testability.
 */
export function shouldTriggerLoad(
  entry: IntersectionObserverEntry | undefined,
  isLoading: boolean,
  hasNextPage: boolean,
  hasOnLoadMore: boolean,
): boolean {
  return Boolean(
    entry?.isIntersecting &&
    !isLoading &&
    hasNextPage &&
    hasOnLoadMore,
  );
}

/**
 * Creates the IntersectionObserver callback handler.
 * Extracted for testability.
 */
export function createIntersectionHandler(
  isLoadingRef: React.MutableRefObject<boolean>,
  hasNextPage: boolean,
  onLoadMore: (() => Promise<void>) | undefined,
): IntersectionObserverCallback {
  return async (entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    
    if (shouldTriggerLoad(entry, isLoadingRef.current, hasNextPage, Boolean(onLoadMore))) {
      isLoadingRef.current = true;
      try {
        await onLoadMore?.();
      } catch {
        // Error handling is delegated to the consumer
        // Just reset the loading state
      } finally {
        isLoadingRef.current = false;
      }
    }
  };
}

/**
 * Hook to manage infinite scroll pagination for Selector component.
 * Uses IntersectionObserver to detect when the user scrolls near the bottom
 * and triggers loading of more options.
 * 
 * @example
 * ```tsx
 * const { sentinelRef } = useSelectorPagination({
 *   listRef,
 *   hasNextPage: true,
 *   isLoadingMore: false,
 *   onLoadMore: async () => await fetchNextPage(),
 *   open: true,
 * });
 * 
 * // In the list component:
 * <ul ref={listRef}>
 *   {options.map(...)}
 *   <div ref={sentinelRef} /> // Sentinel element
 * </ul>
 * ```
 */
export function useSelectorPagination({
  listRef,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
  open,
  threshold = 50,
}: UseSelectorPaginationOptions): UseSelectorPaginationReturn {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isLoadingRef = useRef(false);

  // Keep isLoadingRef in sync with isLoadingMore prop
  useEffect(() => {
    isLoadingRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const handleIntersection = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      
      if (shouldTriggerLoad(entry, isLoadingRef.current, hasNextPage, Boolean(onLoadMore))) {
        isLoadingRef.current = true;
        try {
          await onLoadMore?.();
        } catch {
          // Error handling is delegated to the consumer
          // Just reset the loading state
        } finally {
          isLoadingRef.current = false;
        }
      }
    },
    [hasNextPage, onLoadMore],
  );

  useEffect(() => {
    // Only observe when dropdown is open and there's potential for more data
    if (!open || !hasNextPage || !onLoadMore) {
      return;
    }

    const sentinel = sentinelRef.current;
    const list = listRef.current;

    if (!sentinel || !list) {
      return;
    }

    const observer = new IntersectionObserver(handleIntersection, {
      root: list,
      rootMargin: `0px 0px ${threshold}px 0px`,
      threshold: 0,
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [open, hasNextPage, onLoadMore, listRef, threshold, handleIntersection]);

  return {
    sentinelRef,
  };
}
